import { request } from '../utils/request';
import type {
  SurveyConfigResponse,
  SurveyCategoriesResponse,
  CreateSurveyConfigPayload,
  UpdateSurveyConfigPayload,
} from '../types/survey-config.types';
import {
  getCachedSurveyConfig,
  cacheSurveyConfig,
} from '../services/offline-surveys.service';

export const getSurveyConfig = async (
  accountUuid?: string,
): Promise<SurveyConfigResponse> => {
  // If offline, return cached config
  if (!navigator.onLine) {
    const cached =
      (await getCachedSurveyConfig()) as SurveyConfigResponse | null;
    if (cached) {
      return cached;
    }
    throw new Error('No cached survey config available offline');
  }

  // Online: fetch fresh config and cache it
  const url = accountUuid
    ? `/survey-configs?accountUuid=${accountUuid}`
    : '/survey-configs';
  const response = await request<SurveyConfigResponse>(url);
  await cacheSurveyConfig(response);
  return response;
};

// Fetches categories used in route survey questions
export const getRouteSurveyCategories = async (
  accountUuid?: string,
): Promise<SurveyCategoriesResponse> => {
  const url = accountUuid
    ? `/survey-configs/route-survey-categories?accountUuid=${accountUuid}`
    : '/survey-configs/route-survey-categories';
  return request<SurveyCategoriesResponse>(url);
};

// Fetches categories defined for the account
export const getAccountCategories = async (
  accountUuid?: string,
): Promise<SurveyCategoriesResponse> => {
  const url = accountUuid
    ? `/survey-configs/categories?accountUuid=${accountUuid}`
    : '/survey-configs/categories';
  return request<SurveyCategoriesResponse>(url);
};

export const createSurveyConfig = async (
  accountUuid: string,
  data: CreateSurveyConfigPayload,
): Promise<{ uuid: string }> => {
  return request<{ uuid: string }>(`/survey-configs/${accountUuid}`, {
    method: 'POST',
    body: data,
  });
};

export const updateSurveyConfig = async (
  accountUuid: string,
  data: UpdateSurveyConfigPayload,
): Promise<void> => {
  await request(`/survey-configs/${accountUuid}`, {
    method: 'PUT',
    body: data,
  });
};
